import { useState, useEffect, useRef } from 'react'
import { Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'

interface Command { id: string; label: string; category: string; action: () => void; icon?: React.ReactNode }

interface CommandPaletteProps { commands: Command[]; isOpen: boolean; onClose: () => void; className?: string }

export function CommandPalette({ commands, isOpen, onClose, className }: CommandPaletteProps) {
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => { if (isOpen) { setQuery(''); inputRef.current?.focus() } }, [isOpen])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); if (isOpen) onClose() }
      if (e.key === 'Escape' && isOpen) onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const filtered = query ? commands.filter((c) => c.label.toLowerCase().includes(query.toLowerCase())) : commands
  const grouped = filtered.reduce<Record<string, Command[]>>((acc, cmd) => { (acc[cmd.category] ??= []).push(cmd); return acc }, {})

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh]">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className={cn('relative w-full max-w-lg rounded-xl border border-border bg-background shadow-2xl', className)}>
        <div className="flex items-center gap-2 border-b border-border px-4">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input ref={inputRef} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search..."
            className="flex-1 bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground" />
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>
        <div className="max-h-80 overflow-y-auto p-2">
          {Object.entries(grouped).map(([category, cmds]) => (
            <div key={category}>
              <p className="px-2 py-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{category}</p>
              {cmds.map((cmd) => (
                <button key={cmd.id} onClick={() => { cmd.action(); onClose() }}
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-sm text-foreground hover:bg-accent">
                  {cmd.icon}{cmd.label}
                </button>
              ))}
            </div>
          ))}
          {filtered.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">No results</p>}
        </div>
      </div>
    </div>
  )
}
